import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { isoFormat } from "../../../../../common/helpers/formatDateTime";
import { useAppDispatch } from "../../../../../app/hooks";
import { TalentContinuousEmploymentAccumulation } from "../../../../employment/employmentApi";
import { fetchAccumulationsForTalent } from "../../../../employment/employmentSlice";
import { EmploymentsHierarchyResponse } from "../../../../talents/talentAPI";
import { fetchEmploymentsHierarchy } from "../../../../talents/talentsSlice";
import {
  CONTINUOUS_EMPLOYMENT_STATE_NAMES,
  LeaveAccumulationUnit,
  LEAVE_ACCUMULATION_MAP,
} from "./leaveAccumulationMap";

export interface LeaveAccumulationRow {
  startDate: string;
  value: number;
}

export interface LeaveAccumulationResult {
  rows: LeaveAccumulationRow[];
  unit: LeaveAccumulationUnit;
  labelKey: string;
  loading: boolean;
}

const extractRows = (
  contEmp: TalentContinuousEmploymentAccumulation,
  stateName: string,
  isContinuousType: boolean,
  startDateMap: Map<string, string>,
): LeaveAccumulationRow[] => {
  if (isContinuousType) {
    const value = parseFloat(contEmp.accumulationsByStateName[stateName]);
    return isFinite(value) && value >= 0
      ? [{ startDate: contEmp.dateRange.start, value }]
      : [];
  }

  return Object.entries(contEmp.accumulationsByEmploymentId).flatMap(
    ([empId, stateMap]) => {
      const value = parseFloat(stateMap[stateName]);
      return isFinite(value) && value >= 0
        ? [{ startDate: startDateMap.get(empId) ?? "", value }]
        : [];
    },
  );
};

// Employment start dates are fetched from employmentsHierarchy2 (parallel call) instead of
// AccumulationsForTalentQuery backend, as the latter does not yet include employment date ranges.
// A backend change to AccumulationsForTalentQuery would simplify this to a single API call.
const buildEmploymentStartDateMap = (
  response: EmploymentsHierarchyResponse,
): Map<string, string> => {
  const map = new Map<string, string>();
  for (const cont of response.hierarchy ?? []) {
    for (const emp of cont.employments ?? []) {
      if (emp.employmentId && emp.startDate) {
        map.set(emp.employmentId, emp.startDate);
      }
    }
  }
  return map;
};

const useLeaveAccumulation = (
  talentId: string | null | undefined,
  absenceType: string,
): LeaveAccumulationResult => {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<LeaveAccumulationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const config = LEAVE_ACCUMULATION_MAP[absenceType];

  useEffect(() => {
    const effectConfig = LEAVE_ACCUMULATION_MAP[absenceType];
    if (!talentId || !effectConfig) {
      setRows([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all([
      dispatch(fetchAccumulationsForTalent(talentId)),
      dispatch(fetchEmploymentsHierarchy(talentId)),
    ]).then(([accAction, hierAction]) => {
      if (cancelled) return;

      if (!isFulfilled(accAction)) {
        setLoading(false);
        return;
      }

      const startDateMap = isFulfilled(hierAction)
        ? buildEmploymentStartDateMap(hierAction.payload)
        : new Map<string, string>();

      const isContinuousType = CONTINUOUS_EMPLOYMENT_STATE_NAMES.has(
        effectConfig.stateName,
      );

      const today = format(new Date(), isoFormat);
      const result = accAction.payload.accumulations
        // Only include active continuous employments to avoid showing stale
        // accumulations from ended employment chains.
        // "+999999999-12-31" = open-ended (no end date), always active
        .filter((contEmp) => {
          const end = contEmp.dateRange.end;
          return end.startsWith("+") || end >= today;
        })
        .flatMap((contEmp) =>
          extractRows(contEmp, effectConfig.stateName, isContinuousType, startDateMap),
        )
        .sort((a, b) => {
          if (a.startDate > b.startDate) return -1;
          if (a.startDate < b.startDate) return 1;
          return 0;
        });

      setRows(result);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  // absenceType replaces config as dep (config is derived from static map); dispatch is stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId, absenceType]);

  if (!config) {
    return { rows: [], unit: "days", labelKey: "", loading: false };
  }

  return { rows, unit: config.unit, labelKey: config.labelKey, loading };
};

export default useLeaveAccumulation;
